import React from 'react';
import {StyleSheet, TouchableOpacity, View} from 'react-native';
import Box from '@/Atoms/Box';
import Card from '@/Atoms/Card';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {TablebaseData} from '@/Hooks/OpeningExplorer/useTablebase';
import {
  groupTablebaseMoves,
  moverOutcome,
  TablebaseMove,
} from '../../utils/explorerUtils';

/**
 * Syzygy endgame tablebase panel — replaces the opening explorer in the
 * Analysis tab once the position is down to 7 men, exactly as lichess
 * does. Every legal move is graded Win / Draw / Loss for the side to move,
 * with distance-to-mate/zeroing, grouped and ordered best-first.
 */

const OUTCOME_COLORS = {
  win: '#3DAB9E',
  draw: '#6b584d',
  loss: CCColors.Error,
};

interface TablebasePanelProps {
  data: TablebaseData | undefined;
  isLoading?: boolean;
  isError?: boolean;
  sideToMoveLabel: string; // 'White' | 'Black'
  onMoveClick: (san: string) => void;
  canPlayMoves?: boolean;
}

function positionBanner(data: TablebaseData, side: string) {
  if (data.checkmate) {
    return {text: 'Checkmate', color: OUTCOME_COLORS.loss};
  }
  if (data.stalemate) {
    return {text: 'Stalemate — draw', color: OUTCOME_COLORS.draw};
  }
  if (data.insufficient_material) {
    return {text: 'Insufficient material — draw', color: OUTCOME_COLORS.draw};
  }
  switch (data.category) {
    case 'win':
      return {text: `${side} is winning`, color: OUTCOME_COLORS.win};
    case 'cursed-win':
      return {
        text: `${side} is winning (draw under the 50-move rule)`,
        color: OUTCOME_COLORS.win,
      };
    case 'loss':
      return {text: `${side} is losing`, color: OUTCOME_COLORS.loss};
    case 'blessed-loss':
      return {
        text: `${side} is losing (saved by the 50-move rule)`,
        color: OUTCOME_COLORS.loss,
      };
    case 'draw':
      return {text: 'Drawn position', color: OUTCOME_COLORS.draw};
    default:
      return {text: 'Tablebase position', color: OUTCOME_COLORS.draw};
  }
}

function distanceLabel(move: TablebaseMove): string {
  if (move.checkmate) {
    return 'mate';
  }
  if (move.dtm != null) {
    return `DTM ${Math.abs(move.dtm)}`;
  }
  if (move.dtz != null) {
    return `DTZ ${Math.abs(move.dtz)}`;
  }
  return '';
}

function MoveGroup({
  label,
  color,
  moves,
  onMoveClick,
  canPlayMoves,
  note,
}: {
  label: string;
  color: string;
  moves: TablebaseMove[];
  onMoveClick: (san: string) => void;
  canPlayMoves?: boolean;
  note?: (move: TablebaseMove) => string | null;
}) {
  if (!moves.length) {
    return null;
  }
  return (
    <Box style={{marginTop: 8}}>
      <Box style={styles.groupHead}>
        <View style={[styles.groupDot, {backgroundColor: color}]} />
        <CCText
          fontSize={10.5}
          fontWeight="bold"
          color={color}
          style={styles.groupLabel}>
          {label}
        </CCText>
      </Box>
      {moves.map(move => (
        <TouchableOpacity
          key={move.uci || move.san}
          testID={`tb-move-${move.san}`}
          style={[styles.moveRow, !canPlayMoves && {opacity: 0.55}]}
          disabled={!canPlayMoves}
          onPress={() => onMoveClick(move.san)}>
          <CCText
            fontSize={12.5}
            fontWeight="bold"
            color="#262322"
            style={{width: 70}}>
            {move.san}
          </CCText>
          <CCText fontSize={10.5} color={color} fontWeight="semibold">
            {note?.(move) || distanceLabel(move)}
          </CCText>
        </TouchableOpacity>
      ))}
    </Box>
  );
}

const TablebasePanel: React.FC<TablebasePanelProps> = ({
  data,
  isLoading,
  isError,
  sideToMoveLabel,
  onMoveClick,
  canPlayMoves,
}) => {
  if (isError) {
    return (
      <Card style={styles.container}>
        <CCText fontSize={13} fontWeight="bold" color="#4D3F37">
          Endgame tablebase
        </CCText>
        <CCText fontSize={11} color="#8a8079" style={{marginTop: 6}}>
          Tablebase is busy — it retries automatically.
        </CCText>
      </Card>
    );
  }
  if (!data) {
    return isLoading ? (
      <Card style={styles.container}>
        <CCText fontSize={13} fontWeight="bold" color="#4D3F37">
          Endgame tablebase
        </CCText>
        <CCText fontSize={11} color="#8a8079" style={{marginTop: 6}}>
          Probing 7-man tablebase…
        </CCText>
      </Card>
    ) : null;
  }

  const banner = positionBanner(data, sideToMoveLabel);
  const groups = groupTablebaseMoves(data.moves);
  const fiftyMoveNote = (move: TablebaseMove) => {
    const outcome = moverOutcome(move.category);
    if (outcome === 'cursed-win') {
      return `${distanceLabel(move)} · 50-move rule *`;
    }
    if (outcome === 'blessed-loss') {
      return `${distanceLabel(move)} · 50-move rule *`;
    }
    return null;
  };

  return (
    <Card style={styles.container}>
      <CCText fontSize={13} fontWeight="bold" color="#4D3F37">
        Endgame tablebase
      </CCText>

      <Box
        style={[
          styles.banner,
          {
            borderColor: banner.color + '55',
            backgroundColor: banner.color + '14',
          },
        ]}>
        <CCText fontSize={12} fontWeight="bold" color={banner.color}>
          {banner.text}
        </CCText>
        {data.dtz != null && !data.checkmate && !data.stalemate && (
          <CCText fontSize={10.5} color={banner.color}>
            DTZ {Math.abs(data.dtz)}
            {data.dtm != null ? ` · DTM ${Math.abs(data.dtm)}` : ''}
          </CCText>
        )}
      </Box>

      <MoveGroup
        label="Winning"
        color={OUTCOME_COLORS.win}
        moves={groups.winning}
        onMoveClick={onMoveClick}
        canPlayMoves={canPlayMoves}
        note={fiftyMoveNote}
      />
      <MoveGroup
        label="Drawing"
        color={OUTCOME_COLORS.draw}
        moves={groups.drawing}
        onMoveClick={onMoveClick}
        canPlayMoves={canPlayMoves}
      />
      <MoveGroup
        label="Losing"
        color={OUTCOME_COLORS.loss}
        moves={groups.losing}
        onMoveClick={onMoveClick}
        canPlayMoves={canPlayMoves}
        note={fiftyMoveNote}
      />

      <CCText fontSize={9.5} color="#B9AC9C" style={{marginTop: 10}}>
        Syzygy tablebases via Lichess · * result affected by the 50-move rule
      </CCText>
    </Card>
  );
};

const styles = StyleSheet.create({
  container: {},
  banner: {
    marginTop: 8,
    borderRadius: 8,
    borderWidth: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
    gap: 2,
  },
  groupHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  groupDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  groupLabel: {
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  moveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 5,
    borderTopWidth: 1,
    borderTopColor: 'rgba(38,35,34,0.06)',
  },
});

export default TablebasePanel;
